import React from "react";
import { FormProvider } from "react-hook-form";
import { Button, Dialog, Form } from "@sscs/ui";
import { getClassName } from "@sscs/utils";
import { ButtonColor } from "Types";
import styles from "./dialog.module.scss";
import DialogDangerZone from "./DialogDangerZone";
import useDangerZone from "./useDangerZone";

type PortalDialogContentProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode;
  confirmText: string;
  cancelText?: string;
  confirmProps?: React.ComponentProps<typeof Button>;
  cancelProps?: React.ComponentProps<typeof Button>;
  onConfirm: () => void;
  onCancel: () => void;
  confirmButtonColor?: ButtonColor;
  cancelButtonColor?: ButtonColor;
  dangerZone?: string;
  showCancel?: boolean;
  children?: React.ReactNode;
};

type PortalDialogProps = PortalDialogContentProps & {
  isOpen: boolean;
  maxWidth?: React.ComponentProps<typeof Dialog.Frame>["size"];
  onClose?: () => void;
  className?: string;
};

const PortalDialogContent = ({
  title,
  subtitle,
  meta,
  confirmText,
  cancelText = "Cancel",
  confirmProps,
  cancelProps,
  onConfirm,
  onCancel,
  children,
  confirmButtonColor = "primary",
  cancelButtonColor = "secondary",
  dangerZone,
  showCancel = true,
}: PortalDialogContentProps): React.JSX.Element => {
  const { onSubmit, methods, isValid } = useDangerZone({
    dangerZone,
    onConfirm,
  });

  const titleCls = getClassName({
    [styles["dialog-title-danger-zone"]]: Boolean(dangerZone),
  });

  return (
    <FormProvider {...methods}>
      <Form.Form dense onSubmit={onSubmit}>
        <Dialog.Header className={titleCls}>
          <Dialog.Header.Title id="dialog-title">{title}</Dialog.Header.Title>
          {subtitle && (
            <Dialog.Header.Subtitle>{subtitle}</Dialog.Header.Subtitle>
          )}
        </Dialog.Header>
        <Dialog.Body>
          {children && <div className={styles.confirmation}>{children}</div>}
          <DialogDangerZone dangerZone={dangerZone} />
        </Dialog.Body>
        <Dialog.Footer>
          {meta && <Dialog.Footer.Meta>{meta}</Dialog.Footer.Meta>}
          <Dialog.Footer.Actions>
            {showCancel && (
              <Button
                id="cancel"
                {...cancelProps}
                color={cancelButtonColor}
                onClick={onCancel}
              >
                {cancelText}
              </Button>
            )}
            <Button
              id="confirm"
              {...confirmProps}
              type="submit"
              color={confirmButtonColor}
              disabled={!isValid}
            >
              {confirmText}
            </Button>
          </Dialog.Footer.Actions>
        </Dialog.Footer>
      </Form.Form>
    </FormProvider>
  );
};

const PortalDialog = ({
  isOpen,
  maxWidth = "sm",
  onClose,
  className,
  ...props
}: PortalDialogProps): React.JSX.Element => (
  <Dialog.Frame
    size={maxWidth}
    isOpen={isOpen}
    onClose={onClose}
    className={className}
    aria-labelledby="dialog-title"
  >
    <PortalDialogContent {...props} />
  </Dialog.Frame>
);

export default PortalDialog;
